import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment.dev';
import { ExtractedEntity, IFile, IShareUser } from '../../@core/models/model';
import { ICompareSocketsResponse } from '../../contract-management/components/edit-contract/components/compare/compare-report-view/compare-report-view.component';
import { EMBEDDINGS_GENERATION_STATUS, IDocumentForm, IFormAnswer } from '../../contract-management/models/contract-management.model';
import { QNA_PAIR_METADATA_STATUS } from '../../contract-management/models/settings.model';
import { IAuditLog } from '../../contract-management/services/audit-log.service';
import { IGetContractChecklist } from '../../contract-management/services/contract-checklist-item.service';
import { IGetContractTagMappings } from '../../contract-management/services/contract-tag.service';
import { INotification } from '../../contract-management/services/notification.service';
import { AuthenticationService } from './authentication.service';
import { IAIChatResponse } from 'src/app/contract-management/components/chat/chat.component';
import { TEMPLATE_GENERATION_STATUS } from 'src/app/contract-management/services/template-library.service';
import { IContractApprovalConf, IContractSignatoryConf } from 'src/app/contract-management/models/contract-management.model';
import { IKnowledgeBaseStore } from 'src/app/contract-management/services/knowledgebase.service';
import { CLAUSE_METADATA_STATUS } from 'src/app/contract-management/services/clause-library.service';
import { IGeneralAgentChatResponse } from 'src/app/features/general-agent/general-agent-types';
import { GEOTECHNICAL_SUMMARY_STATUS } from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';

//https://socket.io/docs/v4/typescript/#types-for-the-client

export enum SOCKET_EVENTS {
    NOTIFICATION = 'NOTIFICATION',
    REFRESH_IN_PROGRESS = 'REFRESH_IN_PROGRESS',
    REFRESH_DONE = 'REFRESH_DONE',
    REFRESH_FAILED = 'REFRESH_FAILED',
    REFRESH_WARNING = 'REFRESH_WARNING',
    REPO_FILES_STATUS = 'REPO_FILES_STATUS',
    REPO_TAG_EXTRACTED = 'REPO_TAG_EXTRACTED',
    CONTRACT_TAG_MAPPING = 'CONTRACT_TAG_MAPPING',
    SUMMARY_GENERATION_COMPLETED = 'SUMMARY_GENERATION_COMPLETED',
    SUMMARY_GENERATION_FAILED = 'SUMMARY_GENERATION_FAILED',
    CHECKLIST_ITEMS_EXTRACTED = 'CHECKLIST_ITEMS_EXTRACTED',
    SIGNATORIES_UPDATED = 'SIGNATORIES_UPDATED',
    DOCUMENT_SAVED = 'DOCUMENT_SAVED',
    USER_UPDATED = 'USER_UPDATED',
    AI_TOOLING = 'AI_TOOLING',
    EMBEDDINGS_STATUS_UPDATE = 'EMBEDDINGS_STATUS_UPDATE',
    EMBEDDINGS_GENERATION_STATUS_CHANGED = 'EMBEDDINGS_GENERATION_STATUS_CHANGED',
    COMPARE_KPI_UPDATE = 'COMPARE_KPI_UPDATE',
    // COMPARE_KPI_COMPLETE = 'COMPARE_KPI_COMPLETE',
    // COMPARE_CUSTOM_KPI_UPDATE = 'COMPARE_CUSTOM_KPI_UPDATE',
    DOCUMENT_FORM_QUESTION_EXTRACTED = 'DOCUMENT_FORM_QUESTION_EXTRACTED',
    DOCUMENT_FORM_ANSWER_EXTRACTED = 'DOCUMENT_FORM_ANSWER_EXTRACTED',
    DOCUMENT_FORM_STATUS_CHANGED = 'DOCUMENT_FORM_STATUS_CHANGED',
    ANSWER_GENERATION_STATUS_CHANGED = 'ANSWER_GENERATION_STATUS_CHANGED',
    DOCUMENT_COMPARE_DELETED = 'DOCUMENT_COMPARE_DELETED',
    QNA_PAIR_EXTRACTED = 'QNA_PAIR_EXTRACTED',
    ATTACHMENT_EMBEDDINGS_GENERATION_STATUS_CHANGED = 'ATTACHMENT_EMBEDDINGS_GENERATION_STATUS_CHANGED',
    AUDIT_LOG_CREATED = 'AUDIT_LOG_CREATED',
    GENERAL_AGENT_CHAT = 'GENERAL_AGENT_CHAT',
    GENERAL_AGENT_FILE_EMBEDDINGS_STATUS_CHANGED = 'GENERAL_AGENT_FILE_EMBEDDINGS_STATUS_CHANGED',
    GENERAL_AGENT_THREAD_DELETED = 'GENERAL_AGENT_THREAD_DELETED',
    DOCUMENT_CHAT = 'DOCUMENT_CHAT',
    DOCUMENT_CHAT_THREAD_DELETED = 'DOCUMENT_CHAT_THREAD_DELETED',
    COMPARE_CHAT = 'COMPARE_CHAT',
    COMPARE_CHAT_THREAD_DELETED = 'COMPARE_CHAT_THREAD_DELETED',
    QNA_CHAT = 'QNA_CHAT',
    QNA_CHAT_THREAD_DELETED = 'QNA_CHAT_THREAD_DELETED',
    /** Streaming chat events scoped to a geotechnical analysis report */
    GEOTECHNICAL_CHAT = 'GEOTECHNICAL_CHAT',
    GEOTECHNICAL_CHAT_THREAD_DELETED = 'GEOTECHNICAL_CHAT_THREAD_DELETED',
    AI_CHAT = 'AI_CHAT',
    BROWSER_AGENT_REQUEST = 'BROWSER_AGENT_REQUEST',
    USER_DELETED = 'USER_DELETED',
    ORGANIZATION_DELETED = 'ORGANIZATION_DELETED',
    ADDED_TO_WORKSPACE_SHARE = 'ADDED_TO_WORKSPACE_SHARE',
    CONTRACT_APPROVAL_UPDATED = 'CONTRACT_APPROVAL_UPDATED',
    // ✅ MongoDB + Redis hybrid loading
    REDIS_MESSAGES = 'REDIS_MESSAGES',
    TEMPLATE_GENERATION_STATUS_CHANGED = 'TEMPLATE_GENERATION_STATUS_CHANGED',
    KNOWLEDGE_BASE_UPLOAD_COMPLETED = 'KNOWLEDGE_BASE_UPLOAD_COMPLETED',
    CLAUSE_EXTRACTION_STATUS_CHANGED = 'CLAUSE_EXTRACTION_STATUS_CHANGED',
    THREAD_STATUS_CHANGED = 'THREAD_STATUS_CHANGED',
    GEOTECHNICAL_ANALYSIS_CREATION_STATUS_CHANGED = 'GEOTECHNICAL_ANALYSIS_CREATION_STATUS_CHANGED',
    GEOTECHNICAL_FORMULA_DASHBOARD = 'GEOTECHNICAL_FORMULA_DASHBOARD',
    GEOTECHNICAL_VALUE_ENGINEERING = 'GEOTECHNICAL_VALUE_ENGINEERING',
    GEOTECHNICAL_VENDOR_ANALYSIS_UPDATED = 'GEOTECHNICAL_VENDOR_ANALYSIS_UPDATED',
    GEOTECHNICAL_ANALYSIS_EXCEL_VALIDATION_ERROR = 'GEOTECHNICAL_ANALYSIS_EXCEL_VALIDATION_ERROR',
    GEOTECHNICAL_SUMMARY_STATUS_CHANGED = 'GEOTECHNICAL_SUMMARY_STATUS_CHANGED',
    GEOTECHNICAL_ANALYSIS_SUMMARY_STATUS_CHANGED = 'GEOTECHNICAL_ANALYSIS_SUMMARY_STATUS_CHANGED',
    /** Emitted when AI dashboard HTML generation completes or fails. */
    GEOTECHNICAL_AI_DASHBOARD_STATUS_CHANGED = 'GEOTECHNICAL_AI_DASHBOARD_STATUS_CHANGED',
    SUBSCRIPTION_UPDATED = 'SUBSCRIPTION_UPDATED',
}

export enum CLIENT_EVENTS {
    JOIN_CONTRACT_ROOM = 'JOIN_CONTRACT_ROOM',
    LEAVE_CONTRACT_ROOM = 'LEAVE_CONTRACT_ROOM',
    JOIN_ORG_ROOM = 'JOIN_ORG_ROOM',
    LEAVE_ORG_ROOM = 'LEAVE_ORG_ROOM',
    JOIN_USER_ROOM = 'JOIN_USER_ROOM',
    LEAVE_USER_ROOM = 'LEAVE_USER_ROOM',
    JOIN_COMPARE_ROOM = 'JOIN_COMPARE_ROOM',
    LEAVE_COMPARE_ROOM = 'LEAVE_COMPARE_ROOM',
    JOIN_DOCUMENT_FORM_ROOM = 'JOIN_DOCUMENT_FORM_ROOM',
    LEAVE_DOCUMENT_FORM_ROOM = 'LEAVE_DOCUMENT_FORM_ROOM',
    DOCUMENT_FORM_QUESTION_EXTRACTED = 'DOCUMENT_FORM_QUESTION_EXTRACTED',
    DOCUMENT_FORM_ANSWER_EXTRACTED = 'DOCUMENT_FORM_ANSWER_EXTRACTED',
    JOIN_THREAD_ROOM = 'JOIN_THREAD_ROOM',
    LEAVE_THREAD_ROOM = 'LEAVE_THREAD_ROOM',
    JOIN_GEOTECHNICAL_ANALYSIS_ROOM = 'JOIN_GEOTECHNICAL_ANALYSIS_ROOM',
    LEAVE_GEOTECHNICAL_ANALYSIS_ROOM = 'LEAVE_GEOTECHNICAL_ANALYSIS_ROOM',
    BROWSER_AGENT_RESPONSE = 'BROWSER_AGENT_RESPONSE',
    // ✅ MongoDB + Redis hybrid loading
    GET_REDIS_MESSAGES = 'GET_REDIS_MESSAGES'
}

export interface SERVER_TO_CLIENT_EVENTS {
    noArg: () => void; //example
    basicEmit: (a: number, b: string, c: string) => void; //example
    withAck: (d: string, callback: (e: number) => void) => void; //example
    [SOCKET_EVENTS.NOTIFICATION]: (data: { notification: INotification }) => void;
    [SOCKET_EVENTS.REPO_FILES_STATUS]: (data: { [key: string]: number }) => void;
    [SOCKET_EVENTS.REPO_TAG_EXTRACTED]: (data: { entities: ExtractedEntity[], fileId: string, docTypeId: string, lastElement: boolean }) => void;
    [SOCKET_EVENTS.CONTRACT_TAG_MAPPING]: (data: { mappings: IGetContractTagMappings[], documentInSync: boolean, singleElement: boolean }) => void;
    [SOCKET_EVENTS.REFRESH_IN_PROGRESS]: (data: { username: string }) => void;
    [SOCKET_EVENTS.REFRESH_DONE]: () => void;
    [SOCKET_EVENTS.REFRESH_FAILED]: (data: { message: string, status: boolean }) => void;
    [SOCKET_EVENTS.SUMMARY_GENERATION_COMPLETED]: (data: { summary: string }) => void;
    [SOCKET_EVENTS.SUMMARY_GENERATION_FAILED]: () => void;
    [SOCKET_EVENTS.CHECKLIST_ITEMS_EXTRACTED]: (data: { checklists: IGetContractChecklist[], lastElement?: boolean, singleElement: boolean }) => void;
    [SOCKET_EVENTS.SIGNATORIES_UPDATED]: (data: { contractId: string; signatoryConf: IContractSignatoryConf }) => void;
    [SOCKET_EVENTS.DOCUMENT_SAVED]: (data: { updatedAt: Date }) => void;
    [SOCKET_EVENTS.USER_UPDATED]: (data: { token: string, message?: string }) => void;
    [SOCKET_EVENTS.AI_TOOLING]: (data: { response: string }) => void;
    [SOCKET_EVENTS.EMBEDDINGS_STATUS_UPDATE]: (data: { status: EMBEDDINGS_GENERATION_STATUS, item: string }) => void;
    [SOCKET_EVENTS.EMBEDDINGS_GENERATION_STATUS_CHANGED]: (data: { documentKnowledge: string, status: EMBEDDINGS_GENERATION_STATUS, file?: IFile }) => void;
    [SOCKET_EVENTS.COMPARE_KPI_UPDATE]: (data: ICompareSocketsResponse) => void;
    [SOCKET_EVENTS.DOCUMENT_FORM_QUESTION_EXTRACTED]: (data: { documentForm: string, sections: IDocumentForm['sections'] }) => void;
    [SOCKET_EVENTS.DOCUMENT_FORM_ANSWER_EXTRACTED]: (data: { documentForm: string, answers: IFormAnswer[] }) => void;
    [SOCKET_EVENTS.DOCUMENT_FORM_STATUS_CHANGED]: (data: { documentForm: string, status: IDocumentForm['status'] }) => void;
    [SOCKET_EVENTS.ANSWER_GENERATION_STATUS_CHANGED]: (data: { documentForm: string, answer: IFormAnswer['_id'], status: IFormAnswer['status'] }) => void;
    [SOCKET_EVENTS.DOCUMENT_COMPARE_DELETED]: (data: { compareId: string, message: string }) => void;
    [SOCKET_EVENTS.QNA_PAIR_EXTRACTED]: (data: { organization: string, documentType: string, qnaPairMetadata: string, status: QNA_PAIR_METADATA_STATUS }) => void;
    [SOCKET_EVENTS.AUDIT_LOG_CREATED]: (data: { auditLog: IAuditLog }) => void;
    [SOCKET_EVENTS.GENERAL_AGENT_CHAT]: (data: IGeneralAgentChatResponse[]) => void;
    [SOCKET_EVENTS.GENERAL_AGENT_FILE_EMBEDDINGS_STATUS_CHANGED]: (data: { _id: string, status: EMBEDDINGS_GENERATION_STATUS }) => void;
    [SOCKET_EVENTS.AI_CHAT]: (data: IAIChatResponse) => void;
    [SOCKET_EVENTS.BROWSER_AGENT_REQUEST]: (data: { threadId: string, tool_call_id: string, message_id: string, type: string, useVision?: boolean, tabId?: number, actions?: any[] }) => void;
    [SOCKET_EVENTS.USER_DELETED]: (data: { message: string }) => void;
    [SOCKET_EVENTS.ORGANIZATION_DELETED]: (data: { message: string }) => void;
    [SOCKET_EVENTS.ADDED_TO_WORKSPACE_SHARE]: (data: { workspace: string, sharedUsers: IShareUser[] }) => void;
    [SOCKET_EVENTS.CONTRACT_APPROVAL_UPDATED]: (data: { contractId: string; approvalConf: IContractApprovalConf }) => void;
    [SOCKET_EVENTS.REDIS_MESSAGES]: (data: { threadId: string; messages: any[] }) => void;
    [SOCKET_EVENTS.GENERAL_AGENT_THREAD_DELETED]: (data: { threadId: string; message: string }) => void;
    [SOCKET_EVENTS.DOCUMENT_CHAT]: (data: IGeneralAgentChatResponse) => void;
    [SOCKET_EVENTS.DOCUMENT_CHAT_THREAD_DELETED]: (data: { threadId: string; message: string }) => void;
    /** Streaming response for geotechnical analysis chat threads */
    [SOCKET_EVENTS.GEOTECHNICAL_CHAT]: (data: IGeneralAgentChatResponse) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_CHAT_THREAD_DELETED]: (data: { threadId: string; message: string }) => void;
    [SOCKET_EVENTS.TEMPLATE_GENERATION_STATUS_CHANGED]: (data: { templateId: string, status: TEMPLATE_GENERATION_STATUS }) => void;
    [SOCKET_EVENTS.KNOWLEDGE_BASE_UPLOAD_COMPLETED]: (data: { knowledgeBaseStoreId: string, success: boolean, message: string, knowledgeBase: IKnowledgeBaseStore | null }) => void;
    [SOCKET_EVENTS.CLAUSE_EXTRACTION_STATUS_CHANGED]: (data: { organization: string, clauseMetadataId: string, status: CLAUSE_METADATA_STATUS, metadata?: { attachment?: { filename?: string };[key: string]: unknown } }) => void;
    [SOCKET_EVENTS.CLAUSE_EXTRACTION_STATUS_CHANGED]: (data: { organization: string, clauseMetadataId: string, status: CLAUSE_METADATA_STATUS, metadata?: { attachment?: { filename?: string };[key: string]: unknown } }) => void;
    [SOCKET_EVENTS.THREAD_STATUS_CHANGED]: (data: { threadId: string; status: string; queuePosition?: number | null }) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_FORMULA_DASHBOARD]: (data: { geotechnicalAnalysisId: string; success: boolean; loading?: boolean; error?: string }) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_ANALYSIS_CREATION_STATUS_CHANGED]: (data: { geotechnicalAnalysisId: string; status: 'COMPLETED' | 'FAILED' }) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_VALUE_ENGINEERING]: (data: { geotechnicalAnalysisId: string; success: boolean; loading?: boolean; error?: string }) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_VENDOR_ANALYSIS_UPDATED]: (data: { geotechnicalAnalysisId: string; success: boolean; error?: string }) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_ANALYSIS_EXCEL_VALIDATION_ERROR]: (data: { errorExcel: ArrayBuffer }) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_SUMMARY_STATUS_CHANGED]: (data: { geotechnicalAnalysisId: string; status: GEOTECHNICAL_SUMMARY_STATUS }) => void;
    [SOCKET_EVENTS.GEOTECHNICAL_ANALYSIS_SUMMARY_STATUS_CHANGED]: (data: { geotechnicalAnalysisId: string; status: GEOTECHNICAL_SUMMARY_STATUS }) => void;
    /** Payload when AI dashboard HTML generation completes or fails. */
    [SOCKET_EVENTS.GEOTECHNICAL_AI_DASHBOARD_STATUS_CHANGED]: (data: { geotechnicalAnalysisId: string; success: boolean; html?: string; error?: string }) => void;
    [SOCKET_EVENTS.SUBSCRIPTION_UPDATED]: (data: { organization: string }) => void;

}


export interface CLIENT_TO_SERVER_EVENTS {
    [CLIENT_EVENTS.JOIN_CONTRACT_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.LEAVE_CONTRACT_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.JOIN_ORG_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.LEAVE_ORG_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.JOIN_USER_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.LEAVE_USER_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.JOIN_COMPARE_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.LEAVE_COMPARE_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.JOIN_DOCUMENT_FORM_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.LEAVE_DOCUMENT_FORM_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.JOIN_THREAD_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.LEAVE_THREAD_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.JOIN_GEOTECHNICAL_ANALYSIS_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.LEAVE_GEOTECHNICAL_ANALYSIS_ROOM]: (data: string) => void;
    [CLIENT_EVENTS.BROWSER_AGENT_RESPONSE]: (data: { tool_call_id: string, message_id: string, type: string, state?: any, results?: any[], error?: string }) => void;
    // ✅ MongoDB + Redis hybrid loading
    [CLIENT_EVENTS.GET_REDIS_MESSAGES]: (data: string) => void;
}

@Injectable({ providedIn: 'root' })
export class SocketService {
    private socket !: Socket<SERVER_TO_CLIENT_EVENTS, CLIENT_TO_SERVER_EVENTS>;
    private socketSubject = new BehaviorSubject<Socket<SERVER_TO_CLIENT_EVENTS, CLIENT_TO_SERVER_EVENTS> | null>(null);
    private roomSubscriptions: Map<string, () => void> = new Map();

    constructor(private authenticationService: AuthenticationService) {
        this.authenticationService.currentUser.subscribe(currentUser => {
            if (currentUser) {
                this.socket = io(environment.ioSocketUrl, {
                    transports: ['websocket'],
                    query: { token: localStorage.getItem('token') as string },
                    reconnectionAttempts: Infinity, // Keep trying to reconnect indefinitely
                    reconnection: true,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    timeout: 20000
                });

                // Handle reconnection events
                (this.socket as any).on('reconnect', () => {
                    console.info('Socket reconnected, rejoining rooms...');
                    this.rejoinAllRooms();
                });

                (this.socket as any).on('connect', () => {
                    console.info('Socket connected');
                });

                (this.socket as any).on('disconnect', () => {
                    console.info('Socket disconnected');
                });

                (this.socket as any).on('connect_error', (error: any) => {
                    console.info('❌ Socket connection error:', error);
                });


                this.socketSubject.next(this.socket);

            } else {
                if (this.socket) {
                    this.socket.disconnect();
                }
                this.socketSubject.next(null);
            }

        });
    }

    /**
     * Register a room to be automatically rejoined on reconnection
     * @param roomKey Unique key for the room (e.g., 'org_room', 'user_room', 'contract_123')
     * @param event The socket event to emit (e.g., CLIENT_EVENTS.JOIN_ORG_ROOM)
     * @param payload The payload to send with the event
     */
    registerRoom(roomKey: string, event: CLIENT_EVENTS, payload: string) {
        const roomHandler = async () => {
            const socket = await this.getSocket();
            if (socket && socket.connected) {
                (socket as any).emit(event, payload);
                console.info(`Rejoined room: ${roomKey} with payload: ${payload}`);
            }
        };
        this.roomSubscriptions.set(roomKey, roomHandler);
    }

    /**
     * Unregister a room subscription
     * @param roomKey The unique key for the room
     */
    unregisterRoom(roomKey: string) {
        this.roomSubscriptions.delete(roomKey);
    }

    /**
     * Rejoin all registered rooms (called automatically on reconnection)
     */
    private async rejoinAllRooms() {
        if (this.roomSubscriptions.size === 0) {
            return;
        }

        console.info(`Rejoining ${this.roomSubscriptions.size} room(s)...`);
        const promises = Array.from(this.roomSubscriptions.values()).map(handler => handler());
        await Promise.all(promises);
        console.info('Finished rejoining rooms');
    }

    async getSocket(): Promise<Socket<SERVER_TO_CLIENT_EVENTS, CLIENT_TO_SERVER_EVENTS> | null> {
        const maxRetries = 5;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            if (this.socket) {
                return this.socket;
            }

            if (attempt < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        return null;
    }

    getSocketId() {
        if (this.socket && this.socket.connected) {
            return this.socket.id
        } else {
            return null
        }
    }

    on<T>(event: string): Observable<T[]> {
        return new Observable(observer => {
            let socketInstance: Socket<SERVER_TO_CLIENT_EVENTS, CLIENT_TO_SERVER_EVENTS> | null = null;
            const handler = (...result: T[]) => {
                observer.next(result);
            };

            const socketSubscription = this.socketSubject.subscribe(socket => {
                if (socket && socket !== socketInstance) {
                    // Remove previous listener if socket changed
                    if (socketInstance) {
                        socketInstance.off(event as keyof SERVER_TO_CLIENT_EVENTS, handler as any);
                    }

                    socketInstance = socket;
                    socket.on(event as keyof SERVER_TO_CLIENT_EVENTS, handler as any);
                }
            });

            // Return teardown logic
            return () => {
                if (socketInstance) {
                    socketInstance.off(event as keyof SERVER_TO_CLIENT_EVENTS, handler as any);
                }
                socketSubscription.unsubscribe();
            };
        });
    }
}
